import React, {useEffect, useState} from "react";
import axios from "axios";
import {baseUrl} from "../Univ.js";
import "./IoTStyles.css";
function IoTLiveMonitoring() {
    const [land, setLand] = useState("");
    const [lands, setLands] = useState([]);
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(false);
    // ============================================================
    // LOAD FARMLANDS
    // ============================================================
    const loadLands = async () => {
        try {
            const response = await axios.get(baseUrl + "lands/");
            if (Array.isArray(response.data)) {
                setLands(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setLands(response.data.results);
            } else {
                setLands([]);
            }
        } catch (error) {
            console.log("Farmland Load Error:", error);
            setLands([]);
        }
    };
    // ============================================================
    // LOAD LIVE MONITORING
    // ============================================================
    const loadMonitoring = async () => {
        try {
            setLoading(true);
            let url = baseUrl + "iot-land-monitoring/";
            if (land) {
                url += encodeURIComponent(land) + "/";
            }
            const response = await axios.get(url);
            if (Array.isArray(response.data)) {
                setObservations(response.data);
            } else if (response.data && Array.isArray(response.data.results)) {
                setObservations(response.data.results);
            } else {
                setObservations([]);
            }
        } catch (error) {
            console.log("IoT Monitoring Error:", error);
            setObservations([]);
        } finally {
            setLoading(false);
        }
    };
    // ============================================================
    // INITIAL LOAD
    // ============================================================
    useEffect(() => {
        loadLands();
    }, []);
    // ============================================================
    // LIVE REFRESH
    // ============================================================
    useEffect(() => {
        loadMonitoring();
        const interval = setInterval(loadMonitoring, 5000);
        return () => clearInterval(interval);
    }, [land]);
    // ============================================================
    // GET LATEST OBSERVATION PER DEVICE
    // ============================================================
    const latestByDevice = {};
    observations.forEach(observation => {
        const deviceKey = observation.device || observation.device_id;
        if (deviceKey && !latestByDevice[deviceKey]) {
            latestByDevice[deviceKey] = observation;
        }
    });
    const liveData = Object.values(latestByDevice);
    // ============================================================
    // DISPLAY HELPERS
    // ============================================================
    const getDeviceName = (observation) => {
        if (observation.device_name) {
            return observation.device_name;
        }
        if (observation.device && typeof observation.device === "object") {
            return (observation.device.device_name || observation.device.device_id || "-");
        }
        return (observation.device_id || observation.device || "-");
    };
    const getCropName = (observation) => {
        if (observation.crop_name) {
            return observation.crop_name;
        }
        if (observation.land_crop && typeof observation.land_crop === "object") {
            return (observation.land_crop.crop_name || observation.land_crop.crop || "-");
        }
        return (observation.crop || "-");
    };
    const getLandName = (observation) => {
        if (observation.land_name) {
            return observation.land_name;
        }
        if (observation.land && typeof observation.land === "object") {
            return (observation.land.name || observation.land.land_name || "-");
        }
        return (observation.land || "-");
    };
    return (<section className="container-fluid mt-4">


            {/* ====================================================
                HEADER
            ==================================================== */}

            <div className="iot-page-header">

                <div>

                    <h3>
                        Live Farm IoT Monitoring
                    </h3>

                    <p>
                        Real-time agricultural sensor observations
                    </p>

                </div>


                {/* ==================================================
                    FARMLAND SELECTOR
                ================================================== */}

                <div>

                    <select
                        className="form-control"
                        value={land}
                        onChange={(e) => setLand(e.target.value)}
                    >

                        <option value="">
                            All Farmlands
                        </option>


                        {lands.map(item => (<option
                                key={item.id}
                                value={item.id}
                            >

                                {item.name || item.land_name || item.land_id || `Farmland ${item.id}`}

                            </option>))}

                    </select>

                </div>

            </div>


            {/* ====================================================
                LOADING
            ==================================================== */}

            {loading && (<div className="text-muted mb-3">

                    Updating farm sensor data...

                </div>)}


            {/* ====================================================
                LIVE SENSOR CARDS
            ==================================================== */}

            <div className="row g-3">


                {liveData.length === 0 ? (<div className="col-12">

                        <div className="card shadow-sm">

                            <div className="card-body text-center">

                                <h5>
                                    No live observations
                                </h5>

                                <p className="text-muted">

                                    No IoT sensor data is
                                    currently available
                                    for the selected farmland.

                                </p>

                            </div>

                        </div>

                    </div>) : (liveData.map(observation => (<div
                            className="col-xl-3 col-lg-4 col-md-6"
                            key={observation.id}
                        >

                            <div className="card shadow-sm iot-monitor-card">


                                {/* ==================================================
                                        DEVICE HEADER
                                    ================================================== */}

                                <div className="card-header">

                                    <strong>

                                        {getDeviceName(observation)}

                                    </strong>

                                </div>


                                <div className="card-body">


                                    {/* ==================================================
                                            LOCATION / CROP
                                        ================================================== */}

                                    <div className="mb-3">

                                        <div className="small text-muted">

                                            Farmland

                                        </div>

                                        <strong>

                                            {getLandName(observation)}

                                        </strong>

                                    </div>


                                    <div className="mb-3">

                                        <div className="small text-muted">

                                            Crop

                                        </div>

                                        <strong>

                                            {getCropName(observation)}

                                        </strong>

                                    </div>


                                    {/* ==================================================
                                            SENSOR VALUES
                                        ================================================== */}

                                    <div className="row g-2">


                                        {/* SOIL MOISTURE */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Soil Moisture
                                                </small>

                                                <strong>

                                                    {observation.soil_moisture ?? "--"}

                                                    <span>
                                                            {" "}%
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* SOIL TEMPERATURE */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Soil Temperature
                                                </small>

                                                <strong>

                                                    {observation.soil_temperature ?? "--"}

                                                    <span>
                                                            {" "}°C
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* AIR TEMPERATURE */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Air Temperature
                                                </small>

                                                <strong>

                                                    {observation.air_temperature ?? "--"}

                                                    <span>
                                                            {" "}°C
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* HUMIDITY */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Humidity
                                                </small>

                                                <strong>

                                                    {observation.humidity ?? "--"}

                                                    <span>
                                                            {" "}%
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* LIGHT */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Light
                                                </small>

                                                <strong>

                                                    {observation.light_intensity ?? "--"}

                                                    <span>
                                                            {" "}lux
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* RAINFALL */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Rainfall
                                                </small>

                                                <strong>

                                                    {observation.rainfall ?? "--"}

                                                    <span>
                                                            {" "}mm
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* SOIL PH */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Soil pH
                                                </small>

                                                <strong>

                                                    {observation.soil_ph ?? "--"}

                                                </strong>

                                            </div>

                                        </div>


                                        {/* NITROGEN */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Nitrogen
                                                </small>

                                                <strong>

                                                    {observation.nitrogen ?? "--"}

                                                    <span>
                                                            {" "}mg/kg
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* PHOSPHORUS */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Phosphorus
                                                </small>

                                                <strong>

                                                    {observation.phosphorus ?? "--"}

                                                    <span>
                                                            {" "}mg/kg
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                        {/* POTASSIUM */}

                                        <div className="col-6">

                                            <div className="iot-vital-box">

                                                <small>
                                                    Potassium
                                                </small>

                                                <strong>

                                                    {observation.potassium ?? "--"}

                                                    <span>
                                                            {" "}mg/kg
                                                        </span>

                                                </strong>

                                            </div>

                                        </div>


                                    </div>


                                    {/* ==================================================
                                            RECORD TIME
                                        ================================================== */}

                                    <div className="text-muted small mt-3">

                                        Recorded:
                                        {" "}

                                        {observation.recorded_at}

                                    </div>


                                </div>

                            </div>

                        </div>)))}

            </div>

        </section>);
}
export default IoTLiveMonitoring;
